import {
  type MetaFunction,
  data,
  redirect,
  Link,
  useNavigate,
} from "react-router";
import type { Route } from "./+types/register";
import { hasAuth } from "~/helpers/token.server";
import { z } from "zod";
import { RVFInput } from "~/components/RVF/RVFInput";
import { RVFSubmitButton } from "~/components/RVF/RVFSubmitButton";
import { RVFHiddenInput } from "~/components/RVF/RVFHiddenInput";
import { Turnstile } from "@marsidev/react-turnstile";
import { useState } from "react";
import AppStoreBranding from "~/components/AppStoreBranding";
import Welcome from "~/components/Welcome2";
import { useForm } from "@rvf/react";
import axios from "axios";
import { toast } from "react-toastify";
import type { User } from "~/db/schema/user";
import type { UserDetails } from "~/models/UserDetails";
import type { SubscriptionStatus } from "~/models/SubscriptionStatus";
import { useUserDetails } from "~/helpers/UserContext";
import { useRcStatus } from "~/helpers/RcContext";
import { handleErrorClient } from "~/helpers";

export const meta: MetaFunction = () => {
  return [{ title: "RollCall2 - Register" }];
};

const validator_register = z
  .object({
    firstName: z.string().trim().min(1, "Required!"),
    lastName: z.string().trim().min(1, "Required!"),
    email: z
      .string()
      .trim()
      .min(1, { message: "Required!" })
      .email("Invalid email address"),
    password: z
      .string()
      .trim()
      .min(6, { message: "Password must be at least 6 characters long" })
      .refine(
        (value) => /[a-z]/.test(value),
        "Password must contain a lowercase character",
      )
      .refine(
        (value) => /[A-Z]/.test(value),
        "Password must contain a uppercase character",
      )
      .refine(
        // eslint-disable-next-line no-useless-escape
        (value) => /[!@#\$%\^&\*]/.test(value),
        "Password must contain a special character (!@#$%^&*)",
      )
      .refine((value) => /\d/.test(value), "Password must contain a number"),
    confirmPassword: z.string().trim(),
    "cf-turnstile-response": z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"], // path of error
  });

export const loader = async ({ request, context }: Route.LoaderArgs) => {
  const search = new URL(request.url).search;
  const params = new URLSearchParams(search);
  const isMobileApp = params.get("ismobileapp");
  if (!search || isMobileApp != "true") {
    const authed = await hasAuth(request, context);

    if (authed) {
      return redirect("/people");
    }
  }

  return data({ isMobileApp: isMobileApp === "true" });
};

export default function Index(data: { isMobileApp: boolean }) {
  const [userDetails, setUserDetails] = useUserDetails();
  const [subscriptionStatus, setSubscriptionStatus] = useRcStatus();
  const navigate = useNavigate();
  const [status, setStatus] = useState<string>("");

  const onSubmit = async (values: z.infer<typeof validator_register>) => {
    if (data.isMobileApp) return;
    if (window?.isMobileApp) return;
    try {
      const res = await axios.post<{
        token: string;
        user: User;
        userDetails: UserDetails;
        subscriptionStatus: SubscriptionStatus;
      }>("/auth/signup", {
        email: values.email,
        password: values.password,
        firstName: values.firstName,
        lastName: values.lastName,
        cfTurnstileResponse: values["cf-turnstile-response"],
      });
      const {
        token,
        user,
        userDetails: uDetails,
        subscriptionStatus: subStatus,
      } = res.data;

      setUserDetails(uDetails);
      setSubscriptionStatus(subStatus);
      return navigate("/people");
    } catch (error) {
      const message = await handleErrorClient(error);
      toast.error(message);
    }
  };

  const form = useForm({
    schema: validator_register,
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      confirmPassword: "",
      "cf-turnstile-response": "",
    },
    handleSubmit: (data) => {
      return onSubmit(data);
    },
  });

  return (
    <div className="min-h-[600px] w-full">
      <h1 className="text-center text-4xl max-md:text-3xl">
        Attendance Made Easy
      </h1>

      <div className="flex flex-row flex-wrap justify-around gap-8 max-sm:flex-col-reverse max-sm:items-center">
        <div className="order-none mb-4 max-w-[650px] min-w-[320px] self-auto ps-4">
          <Welcome />
          <AppStoreBranding />
        </div>
        <div
          className="max-sm: mx-4 me-4 flex w-[320px] flex-initial flex-col items-center justify-start self-auto rounded-2xl border border-gray-500 p-3 dark:border-gray-500"
          id="login-form"
        >
          <p id="login-link" className="mb-2">
            Register or{" "}
            <Link className="font-bold" to="/">
              Login
            </Link>
          </p>
          <form {...form.getFormProps()} className="w-[275px]">
            <div className="flex flex-col gap-2">
              <RVFHiddenInput name="subaction" type="hidden" form={form} />
              <RVFInput
                name="firstName"
                label="First Name"
                type="text"
                autoCapitalize="words"
                form={form}
              />
              <RVFInput
                name="lastName"
                label="Last Name"
                type="text"
                autoCapitalize="words"
                form={form}
              />
              <RVFInput name="email" label="Email" type="text" form={form} />
              <RVFInput
                name="password"
                label="Password"
                type="password"
                form={form}
              />
              <RVFInput
                name="confirmPassword"
                label="Confirm Password"
                type="password"
                form={form}
              />
              <div
                // id="cf-turnstile"
                className="flex items-center justify-center"
              >
                <Turnstile
                  siteKey="0x4AAAAAAA_370Mn2aEMBgHB"
                  onError={() => setStatus("error")}
                  onExpire={() => setStatus("expired")}
                  onSuccess={() => setStatus("solved")}
                />
              </div>

              {status === "solved" && <RVFSubmitButton form={form} />}
            </div>
          </form>
        </div>
      </div>
      {/* <CompatibleBrowsers /> */}
    </div>
  );
}
